<template>
  <div class="weather-table">
    <div class="weather-table__header">
      <h5 class="weather-table__title">
        Route Forecast
      </h5>
      <p class="weather-table__subtitle" v-if="hasRoute">
        {{ routesStore.getShipWaypointsCount }} {{ $t('route.totalWP') }}
      </p>
      <p class="weather-table__subtitle" v-else>
        {{ $t('routes.weatherNotAvailable') }}
      </p>
    </div>

    <div class="weather-table__content">
      <b-table
        v-if="hasRoute"
        ref="table"
        :items="routesStore.getShipWaypoints"
        :fields="tableFields"
        striped
        hover
        small
  sticky-header="calc(100vh - 200px)"
        selectable
        select-mode="single"
        head-variant="light"
        :tbody-tr-class="rowClass"
        @row-clicked="onRowClicked"
      >
        <template #cell(seq)="data">
          <span class="smt2s weather-table__muted">{{ data.index }}</span>
        </template>

        <template #cell(eta)="data">
          <span class="smt2s weather-table__muted">{{ formatEta(data.index) }}</span>
        </template>

        <template #cell(temperature)="data">
          <div class="cell-value" :title="cellTooltip(data.index)">
            <b-spinner small type="grow" v-if="isLoading(data.index)" />
            <span v-else-if="hasValue(weatherData[data.index]?.temperature)" class="smt2s">
              {{ weatherData[data.index].temperature }}
            </span>
            <span v-else-if="weatherData[data.index]?.error" class="cell-error">Err</span>
            <span v-else class="smt2s">—</span>
          </div>
        </template>

        <template #cell(wind)="data">
          <div class="cell-value" :title="cellTooltip(data.index)">
            <b-spinner small type="grow" v-if="isLoading(data.index)" />
            <span v-else-if="hasValue(weatherData[data.index]?.windSpeed)" class="smt2s">
              {{ weatherData[data.index].windSpeed }}
            </span>
            <span v-else-if="weatherData[data.index]?.error" class="cell-error">Err</span>
            <span v-else class="smt2s">—</span>
          </div>
        </template>

        <template #cell(direction)="data">
          <div class="cell-value" :title="cellTooltip(data.index)">
            <b-spinner small type="grow" v-if="isLoading(data.index)" />
            <span v-else-if="hasValue(weatherData[data.index]?.windDirection)" class="smt2s">
              {{ weatherData[data.index].windDirection }}
            </span>
            <span v-else-if="weatherData[data.index]?.error" class="cell-error">Err</span>
            <span v-else class="smt2s">—</span>
          </div>
        </template>

        <template #cell(waves)="data">
          <div class="cell-value" :title="cellTooltip(data.index)">
            <b-spinner small type="grow" v-if="isLoading(data.index)" />
            <span v-else-if="hasValue(weatherData[data.index]?.waveHeight)" class="smt2s">
              {{ weatherData[data.index].waveHeight }}
            </span>
            <span v-else-if="weatherData[data.index]?.error" class="cell-error">Err</span>
            <span v-else class="smt2s">—</span>
          </div>
        </template>
      </b-table>

      <div v-else class="weather-table__empty">
        <i class="bi bi-map" />
        <span>{{ $t('routes.weatherNotAvailable') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapStores } from 'pinia';
import { useRoutesStore } from '@/stores/routes';

export default {
  name: 'WeatherTable',
  props: {
    selectedIndex: {
      type: Number,
      default: null
    },
    weatherData: {
      type: Object,
      default: () => ({})
    },
    loadingMap: {
      type: Object,
      default: () => ({})
    },
    hasRoute: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapStores(useRoutesStore),
    tableFields() {
      return [
        { key: 'seq', label: '#', thClass: 'small-gray', tdClass: 'small-black text-center', class: 'col-index' },
        { key: 'eta', label: this.$t('order.eta'), thClass: 'small-gray', tdClass: 'small-black' },
        { key: 'temperature', label: this.$t('order.t') + ' °C', thClass: 'small-gray', tdClass: 'small-black' },
        { key: 'wind', label: this.$t('order.wind') + ' m/s', thClass: 'small-gray', tdClass: 'small-black' },
        { key: 'direction', label: this.$t('order.direction') + ' °', thClass: 'small-gray', tdClass: 'small-black' },
        { key: 'waves', label: 'Wave' + ' m', thClass: 'small-gray', tdClass: 'small-black' }
      ];
    }
  },
  watch: {
    selectedIndex: {
      immediate: true,
      handler(index) {
        this.syncSelection(index);
      }
    },
    hasRoute(newValue) {
      if (!newValue && this.$refs.table) {
        this.$refs.table.clearSelected();
      }
    }
  },
  methods: {
    onRowClicked(item, index) {
      if (!this.hasRoute) {
        return;
      }
      this.$emit('row-click', index);
      this.syncSelection(index);
    },
    syncSelection(index) {
      if (!this.$refs.table) {
        return;
      }

      this.$nextTick(() => {
        this.$refs.table.clearSelected();
        if (index !== null && index >= 0 && index < this.routesStore.getShipWaypointsCount) {
          this.$refs.table.selectRow(index);
        }
      });
    },
    focusRow(index) {
      if (!this.$refs.table) {
        return;
      }

      this.syncSelection(index);

      this.$nextTick(() => {
        const tbody = this.$refs.table.$el.querySelector('tbody');
        if (!tbody) {
          return;
        }
        const rows = tbody.querySelectorAll('tr');
        const row = rows[index];
        if (row) {
          row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    },
    rowClass(item, type) {
      if (type !== 'row') {
        return '';
      }
      const index = item?.id ?? this.routesStore.getShipWaypoints.indexOf(item);
      return index === this.selectedIndex ? 'weather-table__row--selected' : '';
    },
    formatEta(index) {
      const legInfo = this.routesStore.getShipLegInfo(index);
      if (!legInfo) {
        return '—';
      }

      const seconds = legInfo.fromStartSeconds ?? 0;
      const date = this.routesStore.getShipStartDate;
      const time = this.routesStore.getShipStartTime;

      if (!date || !time) {
        return new Date(seconds * 1000).toISOString().substring(11, 16);
      }

      return this.formatSecondsFromStart(seconds);
    },
    formatSecondsFromStart(seconds) {
      const date = this.routesStore.getShipStartDate;
      const time = this.routesStore.getShipStartTime;
      if (!date || !time) {
        return '—';
      }

      const base = dayjs(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
      if (!base.isValid()) {
        return '—';
      }

      return base.add(seconds, 'second').format('DD/MM HH:mm');
    },
    isLoading(index) {
      return !!this.loadingMap[index];
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    cellTooltip(index) {
      const record = this.weatherData[index];
      if (!record?.error) {
        return '';
      }
      return record.error;
    }
  }
};
</script>

<style scoped>
.weather-table {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.weather-table__header {
  padding: 18px 20px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.weather-table__title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f2a44;
  margin-bottom: 6px;
}

.weather-table__subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #60728b;
}

.weather-table__content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.weather-table__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #60728b;
  font-size: 0.9rem;
  gap: 12px;
}

.weather-table__empty i {
  font-size: 1.6rem;
}

:deep(.table) {
  margin-bottom: 0;
}

:deep(thead th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
}

.weather-table__row--selected {
  background-color: rgba(59, 130, 246, 0.08) !important;
}

.cell-value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cell-error {
  color: #d14343;
  font-weight: 600;
  font-size: 0.8rem;
}

.col-index {
  width: 54px;
}

:deep(.table-hover tbody tr:hover) {
  background: rgba(148, 163, 184, 0.12);
}

:deep(.table-striped tbody tr:nth-of-type(odd)) {
  background-color: rgba(248, 250, 252, 0.8);
}

:deep(.b-table-sticky-header) {
  overflow-y: auto;
}

:deep(.table td),
:deep(.table th) {
  vertical-align: middle;
}

:deep(.smt2s) {
  font-size: 0.8rem;
}

.weather-table__muted {
  color: #374151;
}
</style>
